import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Bucket, BucketTheme, Todo, Milestone, BucketType } from '../types';
import { Trash2, CheckCircle2, Circle, Trophy, Target, Edit2, Timer, ArrowUpCircle, CheckSquare, CalendarClock, Clock, Calendar, Plus, Lock, Zap, Crown, Rocket } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useLanguage } from '../i18n';
import { ExternalTodo } from '../App';
import { TaskItem } from './TaskItem';

interface BucketCardProps {
  bucket: Bucket;
  externalTodos?: ExternalTodo[];
  otherBuckets?: Bucket[];
  onUpdateBucket: (id: string, updates: Partial<Bucket>) => void;
  onDeleteBucket: (id: string) => void;
  onEditBucket: (bucket: Bucket) => void;
  onToggleActiveTask: (bucketId: string, todoId: string) => void;
  onManageTask: (bucketId: string, todo?: Todo) => void;
  currentDesign?: string;
}

const THEME_COLORS: Record<BucketTheme, { bg: string; fill: string; accent: string; text: string }> = {
  [BucketTheme.Blue]: { bg: 'bg-blue-950/30', fill: 'from-blue-500 to-cyan-400', accent: 'text-blue-400', text: 'text-blue-50' },
  [BucketTheme.Purple]: { bg: 'bg-purple-950/30', fill: 'from-purple-500 to-fuchsia-400', accent: 'text-purple-400', text: 'text-purple-50' },
  [BucketTheme.Green]: { bg: 'bg-emerald-950/30', fill: 'from-emerald-500 to-green-400', accent: 'text-emerald-400', text: 'text-emerald-50' },
  [BucketTheme.Orange]: { bg: 'bg-orange-950/30', fill: 'from-orange-500 to-amber-400', accent: 'text-orange-400', text: 'text-orange-50' },
  [BucketTheme.Pink]: { bg: 'bg-pink-950/30', fill: 'from-pink-500 to-rose-400', accent: 'text-pink-400', text: 'text-pink-50' },
  [BucketTheme.Red]: { bg: 'bg-red-950/30', fill: 'from-red-600 to-orange-500', accent: 'text-red-400', text: 'text-red-50' },
  [BucketTheme.Teal]: { bg: 'bg-teal-950/30', fill: 'from-teal-500 to-emerald-400', accent: 'text-teal-400', text: 'text-teal-50' },
  [BucketTheme.Yellow]: { bg: 'bg-yellow-950/30', fill: 'from-yellow-400 to-orange-400', accent: 'text-yellow-400', text: 'text-yellow-50' },
  [BucketTheme.Indigo]: { bg: 'bg-indigo-950/30', fill: 'from-indigo-500 to-violet-400', accent: 'text-indigo-400', text: 'text-indigo-50' },
  [BucketTheme.Cyan]: { bg: 'bg-cyan-950/30', fill: 'from-cyan-500 to-sky-400', accent: 'text-cyan-400', text: 'text-cyan-50' },
  [BucketTheme.Lime]: { bg: 'bg-lime-950/30', fill: 'from-lime-500 to-green-400', accent: 'text-lime-400', text: 'text-lime-50' },
  [BucketTheme.Rose]: { bg: 'bg-rose-950/30', fill: 'from-rose-500 to-pink-400', accent: 'text-rose-400', text: 'text-rose-50' },
};

export const BucketCard: React.FC<BucketCardProps> = ({ bucket, externalTodos = [], otherBuckets = [], onUpdateBucket, onDeleteBucket, onEditBucket, onToggleActiveTask, onManageTask, currentDesign }) => {
  const { t } = useLanguage();
  
  const [extensionAmount, setExtensionAmount] = useState(50);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalDeadline, setNewGoalDeadline] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const [currentTick, setCurrentTick] = useState(Date.now());
  const hasActiveTask = useMemo(() => bucket.todos.some(t => !!t.activeSessionStart), [bucket.todos]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (hasActiveTask) {
        interval = setInterval(() => setCurrentTick(Date.now()), 1000);
    } else {
        interval = setInterval(() => setCurrentTick(Date.now()), 60000); 
    }
    return () => clearInterval(interval);
  }, [hasActiveTask]);

  const colors = THEME_COLORS[bucket.theme];

  const isClosed = useMemo(() => {
      if (bucket.type === BucketType.FokusSprint && bucket.endDate) {
          return Date.now() > bucket.endDate;
      }
      return false;
  }, [bucket.type, bucket.endDate]); 

  const displayTodos = useMemo(() => {
      interface DisplayTodoItem extends Todo {
          isExternal: boolean;
          sourceBucketId?: string;
          sourceBucketName?: string;
          sourceBucketTheme?: BucketTheme;
      }

      const local: DisplayTodoItem[] = bucket.todos.map(t => ({ 
          ...t, 
          isExternal: false 
      }));

      const external: DisplayTodoItem[] = externalTodos.map(et => ({
          ...et.todo,
          isExternal: true,
          sourceBucketId: et.sourceBucketId,
          sourceBucketName: et.sourceBucketName,
          sourceBucketTheme: et.sourceBucketTheme
      }));

      return [...local, ...external]
        .filter(t => !t.completed)
        .sort((a, b) => a.createdAt - b.createdAt);
  }, [bucket.todos, externalTodos]);

  const stats = useMemo(() => {
    const allTodos = [...bucket.todos, ...externalTodos.map(et => et.todo)];
    const completedPoints = allTodos.reduce((sum, t) => sum + (t.completed ? (t.points || 1) : 0) + (t.accumulatedPoints || 0), 0);
      
    const LEVEL_STEP = 20;
    const currentLevel = Math.floor(completedPoints / LEVEL_STEP) + 1;
    const levelPoints = completedPoints % LEVEL_STEP;
    const levelPercent = Math.min(100, Math.round((levelPoints / LEVEL_STEP) * 100));

    let percent = 0;
    let target = 0;
    if (bucket.type === BucketType.FokusSprint) {
        target = allTodos.reduce((sum, t) => sum + (t.points || 1), 0);
        percent = target === 0 ? 0 : Math.min(100, Math.round((completedPoints / target) * 100));
    } else if (bucket.goal) {
        target = bucket.goal.targetPoints;
        percent = Math.min(100, Math.round((completedPoints / target) * 100));
    } else {
        percent = levelPercent;
    }
      
    return { percent, completedPoints, currentLevel, levelPercent, target };
  }, [bucket.todos, externalTodos, bucket.goal, bucket.type]);

  // Generate deterministic "trash pieces" for experimental design
  const trashPieces = useMemo(() => {
    if (currentDesign !== 'experimental_original') return [];
    const count = Math.min(450, stats.completedPoints * 4); 
    const pieces = [];
    for (let i = 0; i < count; i++) {
        const left = ((i * 137.5) % 94) + 3; 
        const layer = Math.floor(i / 45); 
        const bottom = (layer * 1.8) + ((i * 17.3) % 5); 
        const rotation = (i * 33.5) % 360;
        const scale = 0.4 + ((i % 5) * 0.12);
        const opacity = 0.15 + ((i % 4) * 0.05); // Significantly more transparent
        const type = i % 4; 
        pieces.push({ left, bottom, rotation, scale, opacity, type, id: i });
    }
    return pieces;
  }, [stats.completedPoints, currentDesign]);

  const timeLeft = useMemo(() => {
    const end = bucket.type === BucketType.FokusSprint ? bucket.endDate : bucket.goal?.deadline;
    if (!end) return null;
    const diff = end - Date.now();
    if (diff < 0) return t.ended;
    return t.daysLeft(Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [bucket.goal, bucket.type, bucket.endDate, t]);

  const toggleTodo = (todoId: string, isExternal: boolean = false, sourceBucketId?: string) => {
    let todo;
    if (isExternal && sourceBucketId) {
        todo = otherBuckets?.find(b => b.id === sourceBucketId)?.todos.find(t => t.id === todoId);
    } else {
        todo = bucket.todos.find(t => t.id === todoId);
    }

    if (!todo || (todo.snoozedUntil && todo.snoozedUntil > Date.now())) return;

    const isCompleting = !todo.completed;
    let timeSpent = todo.timeSpent || 0;
    if (isCompleting && todo.activeSessionStart) timeSpent += (Date.now() - todo.activeSessionStart);

    let updatedTodo: Todo = { ...todo };

    if (todo.targetCount && todo.targetCount > 1 && isCompleting) {
        const newCount = (todo.currentCount || 0) + 1;
        if (newCount < todo.targetCount) {
            updatedTodo = { 
                ...updatedTodo, 
                currentCount: newCount, 
                accumulatedPoints: (todo.accumulatedPoints || 0) + todo.points, 
                activeSessionStart: undefined, 
                timeSpent 
            };
        } else {
            if (todo.recurrence) {
                const now = new Date();
                let nextDate = new Date();
                if (todo.recurrence === 'daily') nextDate.setDate(now.getDate() + 1);
                else if (todo.recurrence === 'weekly') nextDate.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
                else if (todo.recurrence === 'monthly') nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                nextDate.setHours(0, 0, 0, 0);

                updatedTodo = { 
                    ...updatedTodo, 
                    completed: false, 
                    currentCount: 0,
                    accumulatedPoints: (todo.accumulatedPoints || 0) + todo.points, 
                    snoozedUntil: nextDate.getTime(), 
                    activeSessionStart: undefined, 
                    timeSpent 
                };
            } else {
                updatedTodo = { 
                    ...updatedTodo, 
                    completed: true, 
                    currentCount: newCount,
                    completedAt: Date.now(), 
                    activeSessionStart: undefined, 
                    timeSpent 
                };
            }
        }
    } else {
        if (todo.recurrence && isCompleting) {
            const now = new Date();
            let nextDate = new Date();
            if (todo.recurrence === 'daily') nextDate.setDate(now.getDate() + 1);
            else if (todo.recurrence === 'weekly') nextDate.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
            else if (todo.recurrence === 'monthly') nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            nextDate.setHours(0, 0, 0, 0);

            updatedTodo = { ...updatedTodo, completed: false, accumulatedPoints: (todo.accumulatedPoints || 0) + todo.points, snoozedUntil: nextDate.getTime(), activeSessionStart: undefined, timeSpent };
        } else {
            updatedTodo = { ...updatedTodo, completed: isCompleting, completedAt: isCompleting ? Date.now() : undefined, activeSessionStart: undefined, timeSpent: isCompleting ? timeSpent : todo.timeSpent };
        }
    }

    if (isExternal && sourceBucketId) {
        const sourceBucket = otherBuckets?.find(b => b.id === sourceBucketId);
        if (sourceBucket) onUpdateBucket(sourceBucketId, { todos: sourceBucket.todos.map(t => t.id === todoId ? updatedTodo : t) });
    } else {
        onUpdateBucket(bucket.id, { todos: bucket.todos.map(t => t.id === todoId ? updatedTodo : t) });
    }
  };

  const deleteTodo = (todoId: string) => {
    onUpdateBucket(bucket.id, { todos: bucket.todos.filter(t => t.id !== todoId) });
  };

  return (
    <div id={`bucket-${bucket.id}`} className={`relative group w-full h-[550px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl transition-all duration-300 hover:shadow-${bucket.theme}-500/20 backdrop-blur-sm bg-slate-900/50 flex flex-col ${isClosed ? 'grayscale opacity-75' : ''}`}>
      {/* Fokus Sprint Mission Banner */}
      {bucket.type === BucketType.FokusSprint && (
        <div className="absolute top-0 left-0 right-0 h-10 z-20 bg-gradient-to-r from-indigo-600 to-purple-700 flex items-center justify-between px-4 shadow-xl border-b border-white/10 overflow-hidden">
            <div className="absolute inset-0 bg-white/10 mix-blend-overlay animate-pulse"></div>
            <div className="flex items-center gap-2 relative z-10">
                <Rocket size={16} className="text-white animate-bounce" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">{t.typeFokusSprint}</span>
            </div>
            {timeLeft && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/30 border border-white/10 relative z-10">
                    <Timer size={12} className="text-amber-400" />
                    <span className="text-[10px] font-bold text-amber-200">{timeLeft}</span>
                </div>
            )}
        </div>
      )}

      {/* Background Filling Layer (Deep Background) */}
      <div className="absolute inset-0 z-0 h-full pointer-events-none overflow-hidden">
        {currentDesign === 'experimental_original' ? (
          /* Experimental: Paper heap filling (Faded Background) */
          <div className="absolute inset-0 opacity-40">
             {trashPieces.map((p) => (
                <div 
                    key={p.id}
                    className={`absolute animate-piece bg-gradient-to-br ${colors.fill} shadow-sm shadow-black/10`}
                    style={{ 
                        left: `${p.left}%`, 
                        bottom: `${p.bottom}%`, 
                        width: p.type === 1 ? '16px' : p.type === 3 ? '4px' : '10px',
                        height: p.type === 1 ? '4px' : p.type === 3 ? '14px' : '10px',
                        borderRadius: p.type === 2 ? '50%' : '1px',
                        opacity: p.opacity,
                        '--rot': `${p.rotation}deg`,
                        transform: `scale(${p.scale}) rotate(${p.rotation}deg)`
                    } as any}
                />
             ))}
             {/* Depth gradient for bucket interior */}
             <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
          </div>
        ) : (
          /* Original: Liquid filling */
          <div className="absolute bottom-0 left-0 right-0 transition-all duration-1000 ease-in-out z-0" style={{ height: `${stats.percent}%` }}>
            <div className={`absolute inset-0 opacity-40 bg-gradient-to-t ${colors.fill}`}></div>
            {!isClosed && (
                <div className="absolute -top-3 left-0 right-0 h-6 overflow-hidden">
                    <div className="w-[200%] h-full flex animate-wave opacity-40">
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-1/2 h-full fill-current text-white"><path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path></svg>
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-1/2 h-full fill-current text-white"><path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path></svg>
                    </div>
                </div>
            )}
          </div>
        )}
      </div>

      {bucket.headerImage && (
        <div className="absolute top-0 left-0 right-0 h-48 z-1 pointer-events-none">
           <img src={bucket.headerImage} alt="" className="w-full h-full object-cover opacity-30 mix-blend-overlay" />
           <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
        </div>
      )}

      {isClosed && (
          <div className="absolute inset-0 z-40 pointer-events-none flex flex-col items-center justify-center p-6 text-center backdrop-blur-sm bg-black/20">
              <div className="bg-slate-900/80 p-5 rounded-3xl border border-white/10 shadow-2xl">
                  <Lock size={32} className="text-white/50 mb-2 mx-auto" />
                  <h3 className="text-xl font-bold text-white mb-1">{t.bucketClosed}</h3>
                  <p className="text-sm text-slate-300 max-w-xs">{t.bucketClosedDesc}</p>
              </div>
          </div>
      )}

      {/* Main Content (Foreground Shell) */}
      <div className={`relative z-10 flex flex-col h-full p-6 ${bucket.type === BucketType.FokusSprint ? 'pt-14' : ''}`}>
        <div className="flex justify-between items-start mb-4">
          <div className="min-w-0 flex-1">
            <h3 className={`text-2xl font-bold ${colors.text} tracking-tight drop-shadow-lg flex items-center gap-2 truncate`}>{bucket.name}</h3>
            
            {bucket.description && (
                <p className="text-[11px] text-white/60 line-clamp-2 mt-1 italic leading-tight">{bucket.description}</p>
            )}

            <div className="flex items-center gap-1.5 mt-3 mb-1.5">
                <div className="relative overflow-hidden flex items-center gap-2 bg-slate-950/80 text-indigo-300 px-3 py-1 rounded-full border border-white/10 shadow-lg min-w-[120px]">
                    <div className="absolute inset-y-0 left-0 bg-indigo-500/30 border-r border-indigo-400/40 transition-all duration-1000 ease-out z-0" style={{ width: `${stats.levelPercent}%` }}></div>
                    <Crown size={12} className="text-amber-400 relative z-10" />
                    <span className="font-bold relative z-10 text-xs">{stats.currentLevel}</span>
                    <span className="w-px h-3 bg-white/10 mx-0.5 relative z-10"></span>
                    <span className="uppercase tracking-wider text-[10px] font-black relative z-10 drop-shadow-sm">{t.levelTitles[Math.min(stats.currentLevel - 1, t.levelTitles.length - 1)]}</span>
                </div>
            </div>

            {bucket.type === BucketType.FokusSprint && bucket.startDate && bucket.endDate ? (
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-white/70 mb-1 flex items-center gap-1.5"><Calendar size={12} />{new Date(bucket.startDate).toLocaleDateString()} - {new Date(bucket.endDate).toLocaleDateString()}</p>
                  <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-white/60"><span>{stats.completedPoints} / {stats.target} {t.pts}</span></div>
                </div>
            ) : bucket.goal?.title ? (
                 <div className="flex flex-col gap-0.5">
                     <p className="text-sm font-medium text-yellow-500/90 flex items-center gap-1.5"><Target size={12} />{bucket.goal.title}</p>
                     <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-white/60"><span>{stats.completedPoints} / {stats.target} {t.pts}</span>{timeLeft && <><span className="mx-1">•</span><span className="flex items-center gap-1"><Timer size={10} /> {timeLeft}</span></>}</div>
                 </div>
            ) : null}
          </div>
          <div className="flex gap-2 shrink-0">
             <button onClick={() => onEditBucket(bucket)} title={t.editBucket} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white/90 transition-colors backdrop-blur-md shadow-lg relative z-40"><Edit2 size={16} /></button>
            <button onClick={() => onDeleteBucket(bucket.id)} className="p-2 rounded-full bg-white/10 hover:bg-red-500/20 hover:text-red-400 text-white/70 transition-colors backdrop-blur-md shadow-lg"><Trash2 size={16} /></button>
          </div>
        </div>
        
        {/* Task List container - Enhanced Foreground Presence */}
        <div className={`flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-2.5 rounded-2xl p-2 transition-all ${currentDesign === 'experimental_original' ? 'bg-slate-900/60 backdrop-blur-md border border-white/10 shadow-[inset_0_1px_4px_rgba(255,255,255,0.05)]' : ''}`} ref={scrollRef}>
          {displayTodos.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-white/20"><p className="text-sm text-center whitespace-pre-line font-medium">{t.emptyBucket}</p></div>
          ) : (
            displayTodos.map((todo) => (<TaskItem key={todo.id} todo={todo} bucketId={bucket.id} bucketName={bucket.name} bucketTheme={todo.isExternal && todo.sourceBucketTheme ? todo.sourceBucketTheme : bucket.theme} isExternal={todo.isExternal} sourceBucketName={todo.sourceBucketName} isClosed={isClosed} isActive={!!todo.activeSessionStart} currentTick={currentTick} onToggle={() => toggleTodo(todo.id, todo.isExternal, todo.sourceBucketId)} onToggleTimer={() => onToggleActiveTask(todo.isExternal ? todo.sourceBucketId! : bucket.id, todo.id)} onEdit={() => onManageTask(bucket.id, todo)} onDelete={() => deleteTodo(todo.id)} />))
          )}
        </div>
        
        {!isClosed && (
            <div className="mt-4 relative z-20">
                <button 
                    onClick={() => onManageTask(bucket.id)} 
                    className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-500 border border-indigo-400/30 rounded-2xl text-white font-bold transition-all flex items-center justify-center gap-3 group shadow-xl shadow-indigo-950/20 active:scale-[0.98]"
                >
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Plus size={16} />
                    </div>
                    {t.addTask}
                </button>
            </div>
        )}
      </div>
    </div>
  );
};